import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../axiosConfig";
import { IconClear, IconExport, typeIcons } from "./Utils";
import { useUserPermissions } from "../hooks/useUserPermissions";
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaCircleNotch,
  FaTimesCircle,
} from "react-icons/fa";
import "./TaskPanel.css";

const TaskPanel = ({
  isOpen,
  user,
  loading,
  onIndicatorChange,
  refreshCount,
}) => {
  const { permissions, loading: permissionsLoading } = useUserPermissions();
  const isAdministrator =
    !permissionsLoading && Boolean(permissions?.is_administrator);
  const [celeryTasksData, setCeleryTasksData] = useState(null);
  const [celeryTasksError, setCeleryTasksError] = useState(null);
  const lastCeleryPayloadRef = useRef(null);
  const [expandedTaskIds, setExpandedTaskIds] = useState(() => ({}));
  const forcePollInFlightRef = useRef(false);

  // Recurring poll for Celery task statuses
  useEffect(() => {
    if (loading || !user) return;

    let cancelled = false;
    let activeController = null;
    let inFlight = false;

    const poll = async () => {
      if (inFlight) return;
      inFlight = true;

      activeController = new AbortController();
      try {
        const resp = await axios.get("/celery-tasks/", {
          params: { limit: 100, offset: 0 },
          signal: activeController.signal,
        });

        if (!cancelled) {
          setCeleryTasksError(null);

          // Avoid unnecessary re-renders if payload hasn't changed
          const payloadStr = JSON.stringify(resp.data);
          if (payloadStr !== lastCeleryPayloadRef.current) {
            lastCeleryPayloadRef.current = payloadStr;
            setCeleryTasksData(resp.data);
          }
        }
      } catch (err) {
        if (!cancelled) {
          // Ignore aborts; otherwise keep a minimal error message
          if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
            setCeleryTasksError("Failed to load tasks");
          }
        }
      } finally {
        inFlight = false;
        activeController = null;
      }
    };

    // Initial fetch + every 5 seconds
    poll();
    const intervalId = setInterval(poll, 5000);

    return () => {
      cancelled = true;
      if (activeController) activeController.abort();
      clearInterval(intervalId);
    };
  }, [loading, user]);

  // Fire an immediate extra poll when an external action triggers a refresh
  useEffect(() => {
    if (!refreshCount || loading || !user) return;
    if (forcePollInFlightRef.current) return;
    let cancelled = false;
    const controller = new AbortController();
    forcePollInFlightRef.current = true;
    (async () => {
      try {
        const resp = await axios.get("/celery-tasks/", {
          params: { limit: 100, offset: 0 },
          signal: controller.signal,
        });
        if (!cancelled) {
          setCeleryTasksError(null);
          const payloadStr = JSON.stringify(resp.data);
          if (payloadStr !== lastCeleryPayloadRef.current) {
            lastCeleryPayloadRef.current = payloadStr;
            setCeleryTasksData(resp.data);
          }
        }
      } catch (err) {
        if (
          !cancelled &&
          err?.name !== "CanceledError" &&
          err?.code !== "ERR_CANCELED"
        ) {
          setCeleryTasksError("Failed to load tasks");
        }
      } finally {
        forcePollInFlightRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [refreshCount, loading, user]);

  const formatTaskField = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  const getTaskCardStatus = (task) => {
    const status = (task?.status || "").toString().toLowerCase();
    const result = (task?.result || "").toString().toLowerCase();

    if (status === "pending") return "pending";
    if (result === "success") return "complete";
    if (result === "fail" || result === "failure") return "fail";
    if (status === "completed") return "complete";
    return null;
  };

  const TASK_TYPE_LABELS = {
    create_slideshows: {
      pending: "Exporting slides",
      complete: "Exported slides",
      fail: "Export failed",
    },
    create_slideshows_test: {
      pending: "Testing slide export",
      complete: "Tested slide export",
      fail: "Slide export test failed",
    },
    apply_imported_slides_test: {
      pending: "Testing slide import",
      complete: "Tested slide import",
      fail: "Slide import test failed",
    },
    create_proposal: {
      pending: "Creating proposal",
      complete: "Proposal created",
      fail: "Proposal failed",
    },
    create_booking_form: {
      pending: "Creating booking form",
      complete: "Booking form created",
      fail: "Booking form failed",
    },
    apply_imported_slides: {
      pending: "Importing slides",
      complete: "Imported slides",
      fail: "Import failed",
    },
    delete_slides_from_pptx: {
      pending: "Deleting slide",
      complete: "Deleted slide",
      fail: "Delete failed",
    },
    generate_handout: {
      pending: "Generating handout",
      complete: "Handout generated",
      fail: "Handout generation failed",
    },
  };

  const getTaskTypeIcon = (taskType) => {
    const normalized = (taskType || "").toString().trim();

    if (normalized === "apply_imported_slides" || normalized === "apply_imported_slides_test") {
      const importIcon = typeIcons["import"];
      return React.isValidElement(importIcon)
        ? React.cloneElement(importIcon, { className: undefined })
        : importIcon;
    }
    if (
      normalized === "create_slideshows" ||
      normalized === "create_slideshows_test"
    )
      return <IconExport aria-hidden="true" />;
    if (normalized === "delete_slides_from_pptx")
      return <IconClear aria-hidden="true" />;
    if (normalized === "generate_handout") {
      const handoutIcon = typeIcons["createhandouts"];
      return React.isValidElement(handoutIcon)
        ? React.cloneElement(handoutIcon, { className: undefined })
        : handoutIcon;
    }

    return null;
  };

  const getTaskTypeLabel = (taskType, cardStatus) => {
    if (!taskType) return "(no type)";
    const map = TASK_TYPE_LABELS[taskType];
    if (!map) return taskType;
    if (cardStatus === "complete") return map.complete;
    if (cardStatus === "fail") return map.fail;
    return map.pending;
  };

  const getUserPrefix = (email) => {
    if (!email || typeof email !== "string") return "";
    return email.split("@")[0] || "";
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return isoString;

    const now = new Date();
    const isToday =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();

    const time = d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
    if (isToday) return time;

    const date = d.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
    return `${date} ${time}`;
  };

  const toggleTaskDetails = (taskId) => {
    setExpandedTaskIds((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const getMostRecentTaskIndicator = (task) => {
    if (!task) return null;

    const status = (task.status || "").toString().toLowerCase();
    if (status === "pending") return "pending";

    const rawResult = task.result;
    let resultText = "";
    if (typeof rawResult === "string") {
      resultText = rawResult;
    } else if (rawResult && typeof rawResult === "object") {
      if (typeof rawResult.status === "string") resultText = rawResult.status;
      else if (typeof rawResult.result === "string")
        resultText = rawResult.result;
    } else if (rawResult !== null && rawResult !== undefined) {
      resultText = String(rawResult);
    }

    const normalizedResult = (resultText || "").toLowerCase();
    if (normalizedResult === "success") return "success";
    if (normalizedResult === "fail" || normalizedResult === "failure")
      return "fail";
    return null;
  };

  const mostRecentTaskIndicator = useMemo(() => {
    const mostRecentTask = celeryTasksData?.results?.[0] || null;
    return getMostRecentTaskIndicator(mostRecentTask);
  }, [celeryTasksData]);

  useEffect(() => {
    if (typeof onIndicatorChange === "function") {
      onIndicatorChange(mostRecentTaskIndicator);
    }
  }, [mostRecentTaskIndicator, onIndicatorChange]);

  if (!isOpen) return null;

  return (
    <aside className="taskpanel" aria-label="Right panel">
      <div className="taskpanel-title">Tasks</div>

      {celeryTasksError && (
        <div className="taskpanel-muted">{celeryTasksError}</div>
      )}

      {!celeryTasksData ? (
        <div className="taskpanel-muted">Loading tasks...</div>
      ) : (celeryTasksData.results || []).length === 0 ? (
        <div className="taskpanel-muted">No tasks found.</div>
      ) : (
        (celeryTasksData.results || []).map((t) => {
          const cardStatus = getTaskCardStatus(t);
          const taskType = formatTaskField(t.task_type);
          const taskTypeIcon = getTaskTypeIcon(taskType);
          const taskTypeLabel = getTaskTypeLabel(taskType, cardStatus);

          let affectedSlides = [];
          let deletedSlide = null;
          let exportSessions = [];
          if (t.arg) {
            try {
              const parsed = JSON.parse(t.arg);
              if (
                taskType === "apply_imported_slides" &&
                Array.isArray(parsed.affected)
              ) {
                affectedSlides = parsed.affected;
              }
              if (
                taskType === "delete_slides_from_pptx" &&
                parsed.file &&
                parsed.slide_number != null
              ) {
                deletedSlide = {
                  file: parsed.file,
                  slide_number: parsed.slide_number,
                };
              }
              if (
                taskType === "create_slideshows" &&
                Array.isArray(parsed.sessions) &&
                parsed.slide_names
              ) {
                exportSessions = parsed.sessions.map((s) => ({
                  filename: s.filename || "",
                  names: (s.slides || []).map(
                    (id) => parsed.slide_names[String(id)] || String(id),
                  ),
                }));
              }
            } catch {}
          }

          return (
            <div key={t.task_id} className="taskpanel-task-card">
              <div className="taskpanel-task-header">
                <div className="taskpanel-task-title">
                  <span
                    className={`taskpanel-task-status-icon taskpanel-task-status-icon-${
                      cardStatus || "none"
                    }`}
                    aria-hidden="true"
                  >
                    {cardStatus === "pending" ? (
                      <FaCircleNotch className="taskpanel-task-status-icon-inner taskpanel-task-status-spin" />
                    ) : cardStatus === "complete" ? (
                      <FaCheckCircle className="taskpanel-task-status-icon-inner" />
                    ) : cardStatus === "fail" ? (
                      <FaTimesCircle className="taskpanel-task-status-icon-inner" />
                    ) : null}
                  </span>

                  <span className="taskpanel-task-type-wrap">
                    {taskTypeIcon && (
                      <span
                        className="taskpanel-task-type-icon"
                        style={{ fontSize: "0.95rem" }}
                        aria-hidden="true"
                      >
                        {taskTypeIcon}
                      </span>
                    )}
                    <span className="taskpanel-task-type">{taskTypeLabel}</span>
                  </span>
                </div>
              </div>

              <div className="taskpanel-task-meta-row">
                <div className="taskpanel-task-meta">
                  <span className="taskpanel-task-meta-user">
                    {getUserPrefix(formatTaskField(t.user))}
                  </span>
                  <span className="taskpanel-task-meta-sep">•</span>
                  <span className="taskpanel-task-meta-time">
                    {formatTime(formatTaskField(t.created_at))}
                  </span>
                </div>

                {isAdministrator && (
                  <button
                    type="button"
                    className="taskpanel-task-toggle"
                    onClick={() => toggleTaskDetails(t.task_id)}
                  >
                    {expandedTaskIds[t.task_id] ? (
                      <>
                        <FaChevronUp className="taskpanel-task-toggle-icon" />{" "}
                        Hide
                      </>
                    ) : (
                      <>
                        <FaChevronDown className="taskpanel-task-toggle-icon" />{" "}
                        Details
                      </>
                    )}
                  </button>
                )}
              </div>

              {affectedSlides.length > 0 && (
                <div className="taskpanel-task-affected">
                  {affectedSlides.map((item, i) => {
                    const verb =
                      item.sort_operation === "replace"
                        ? "replacing"
                        : "added after";
                    return (
                      <div key={i} className="taskpanel-task-affected-item">
                        <span className="taskpanel-task-affected-src">
                          Slide {item.slide_number}
                        </span>{" "}
                        <span className="taskpanel-task-affected-verb">
                          {verb}
                        </span>{" "}
                        <span className="taskpanel-task-affected-dest">
                          {item.sort_file} Slide {item.sort_number}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {deletedSlide && (
                <div className="taskpanel-task-affected">
                  <div className="taskpanel-task-affected-item">
                    <span className="taskpanel-task-affected-verb">
                      Deleted
                    </span>{" "}
                    <span className="taskpanel-task-affected-dest">
                      {deletedSlide.file} Slide {deletedSlide.slide_number}
                    </span>
                  </div>
                </div>
              )}

              {exportSessions.length > 0 && (
                <div className="taskpanel-task-slide-list">
                  {exportSessions.map((session, si) => (
                    <div key={si}>
                      {exportSessions.length > 1 && (
                        <div className="taskpanel-task-slide-list-session">
                          {session.filename}
                        </div>
                      )}
                      {session.names.map((name, ni) => (
                        <div
                          key={ni}
                          className="taskpanel-task-slide-list-item"
                        >
                          {name || "(unnamed)"}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {expandedTaskIds[t.task_id] && (
                <div className="taskpanel-task-details">
                  <div className="taskpanel-task-row">
                    <span className="taskpanel-task-label">Task ID</span>
                    <span className="taskpanel-task-value">
                      {formatTaskField(t.task_id)}
                    </span>
                  </div>

                  <div className="taskpanel-task-row">
                    <span className="taskpanel-task-label">Arg</span>
                    <span className="taskpanel-task-value">
                      {formatTaskField(t.arg)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </aside>
  );
};

export default TaskPanel;
